import { all, dropTask } from 'ember-concurrency';
import { or, eq, not } from 'tio-ui/utilities';
import Button from 'tio-reimagined/components/button';
import Divider from 'tio-reimagined/components/divider';
import { fn } from '@ember/helper';
import { hasInProgressInstanceOfCurrentProgramTemplate } from 'tio-common/utils/tuition-assistance/tas-grouping';
import {
  abandonProgramRequest,
  requestProgramApprovalRequest,
} from 'tio-common/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-common/utils/tas-actor';
import Input from 'tio-reimagined/components/input';
import ReSelect from 'tio-reimagined/components/select';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { formatDateToISO } from 'tio-common/utils/date';
import Footer from 'tio-reimagined/components/footer';
import Modal from 'tio-reimagined/components/modal';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import { trackedObject } from '@ember/reactive/collections';
import {
  getAllValuesForTasField,
  copyFieldsAndUpdatePerProgramTemplate,
  getSingleValueForTasField,
  setSingleValueForTasField,
  getConfigForTasField,
  isTasFieldComponentType,
  type FieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { Header, HStack, Section, VStack } from 'tio-reimagined/components/layout';
import { LinkTo } from '@ember/routing';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import inputmask from 'tio-common/modifiers/inputmask';
import { getInputmaskAlias, getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import ProgramFormField from 'tio-common/components/tuition-assistance/program-form-field';
import SchoolSelect from 'tio-common/components/tuition-assistance/program-details/school-select';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import TuitionAssistanceFormsCustomFieldsForProvince from 'tio-common/components/tuition-assistance/forms/custom-fields-for-province';
import LpnInfoCard from 'tio-common/components/lpn/lpn-info-card';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/models/tas-program-instance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type ConditionModel from 'tio-data/models/condition';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasGroupModel from 'tio-data/models/tas-group';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasProgramTemplateInstitutionModel from 'tio-data/models/tas-program-template-institution';
import type TasUnifiedInstitutionModel from 'tio-data/models/tas-unified-institution';
import type { TasUnifiedInstitution } from 'tio-data/models/search';
// import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type Owner from '@ember/owner';
import type AgreementModel from 'tio-data/models/agreement';
// import type IntlService from 'ember-intl/services/intl';
import {
  PhCheckCircle,
  PhCircle,
  PhCaretLeft,
  PhTrash,
  PhClipboardText,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import type LpnPage from 'tio-data/models/lpn-page';
import type PartnerService from 'tio-employee/services/partner';
import { normalizeDate } from 'tio-common/utils/date/normalize-date';

const number = Number;

export interface NewTypeOneOrFourSignature {
  Args: {
    model: {
      activeInstances: TASProgramInstanceModel[];
      programInstance: TASProgramInstanceModel;
      selectedSchool: TasUnifiedInstitutionModel | undefined;
      tasGroups: TasGroupModel[];
    };
  };
}

type ProgramInstanceFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FormField = {
  name: ProgramInstanceFieldName;
  mask: string | null;
  type: string | null;
  rules: {
    type: string;
    typeError?: string;
  };
  disabled: boolean;
};

export default class NewTypeOneOrFourComponent extends Component<NewTypeOneOrFourSignature> {
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked customFieldsCopy: CustomFieldSignature[] = [];
  @tracked isDeleteProgramOpen = false;
  @tracked isOpen = true;
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked hasSubmitted = false;
  @tracked submitError = null;
  @tracked requiredCustomFields = false;
  @tracked isLpnSchoolSelected = false;
  @tracked highlightedSection: string | null = null;
  programFieldsCopy;

  scrollTo = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.highlightedSection = sectionId;
    setTimeout(() => (this.highlightedSection = null), 2000);
  };

  // @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare partner: PartnerService;

  // Type guards for TasUnifiedInstitution properties
  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  constructor(owner: Owner, args: NewTypeOneOrFourSignature['Args']) {
    super(owner, args);
    this.programFieldsCopy = trackedObject(args.model.programInstance.fields);
    const programTemplateCustomFields =
      args.model.programInstance.tasProgramTemplate?.instanceCustomFields || [];
    this.customFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      args.model.programInstance.customFields,
      programTemplateCustomFields
    );
    this.setSelectedSchoolIfExists();
    this.updateCustomRequiredFields(this.customFieldsCopy);
  }

  setSelectedSchoolIfExists() {
    if (this.args.model.selectedSchool) {
      this.selectedSchool = this.args.model.selectedSchool;
    }
  }

  updateDateFieldValue = (fieldName: ProgramInstanceFieldName, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      // @ts-expect-error - ProgramInstanceFieldName not compatible with setSingleValueForTasField signature
      setSingleValueForTasField(fieldName, isoDate, this.programFieldsCopy);
    }
  };

  // TAS.TODO: need custom field validation
  get customFieldValidationErrors() {
    return [];
  }

  get programFormFields(): FormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      { name: 'NAME', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      { name: 'EMPLOYEE_ID', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      { name: 'FT_PT_OTHER', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      {
        name: 'EMPLOYEE_DOB',
        mask: null,
        type: 'date',
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'EMPLOYEE_EMAIL',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'SCHOOL_INSTITUTION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      { name: 'STUDENT_ID', mask: null, type: null, rules: { type: 'string' }, disabled: false },
      {
        name: 'PROGRAM_SPECIALIZATION',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      { name: 'PROGRAM_MAJOR', mask: null, type: null, rules: { type: 'string' }, disabled: false },
      {
        name: 'ESTIMATED_COST',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
        disabled: false,
      },
      {
        name: 'ENROLLMENT_OBJECTIVE',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'ESTIMATED_PROGRAM_BEGIN',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
      },
      {
        name: 'ESTIMATED_PROGRAM_COMPLETION',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
      },
    ];
  }

  get programTemplate() {
    return this.args.model.programInstance.tasProgramTemplate;
  }

  isSchoolSelectProgramField = (fieldName: ProgramInstanceFieldName) => {
    return isTasFieldComponentType(fieldName, this.programTemplate.fields, 'school-select');
  };

  get shouldShowAttachments() {
    return (
      this.programTemplate.attachmentsForProgramApprovalVisible ||
      this.args.model.programInstance.state === 'TAS.ProgramInstanceState.PRIMED'
    );
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    return this.programTemplate.requireAttachmentsForProgramApproval;
  }

  get hasRequiredAttachments() {
    if (this.isAttachmentRequired) {
      return !!this.args.model.programInstance.tasAssets?.length;
    }
    return true;
  }

  get attachmentsListDescription() {
    return getAllValuesForTasField(
      'PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
      this.programTemplate.fields
    ).join(', ');
  }

  get conditionsForSubmit() {
    return this.programTemplate?.programApproveConditions || [];
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  get eligibleBasedOnWaitingPeriod() {
    return true;
    // return !this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
    //   this.eligibilityWaitingPeriod,
    //   this.args.model.programInstance.employee?.tasParticipant
    // );
  }

  // get eligibilityWaitingPeriod() {
  //   return (getSingleValueForTasField('ELIGIBILITY_WAITING_PERIOD', this.programTemplate.fields) ||
  //     0) as number;
  // }

  // get dateEligible() {
  //   const date = this.tuitionAssistance.waitingPeriodEndDate(
  //     this.eligibilityWaitingPeriod,
  //     this.args.model.programInstance.employee?.tasParticipant
  //   );
  //   return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  // }

  get isActiveParticipant() {
    return this.args.model.programInstance.employee?.tasParticipant?.isActive || false;
  }

  get canSubmitApplication() {
    return (
      this.isActiveParticipant &&
      this.args.model.programInstance.state === 'TAS.ProgramInstanceState.DRAFT' &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      this.isFormValid &&
      this.requiredCustomFields
    );
  }

  get isFormValid() {
    let fieldValues = [];

    for (const field of this.programFormFields) {
      const config = getConfigForTasField(field.name, this.programTemplate.fields);
      if (config.required) {
        const value = getSingleValueForTasField(field.name, this.programFieldsCopy);
        fieldValues.push(value);
      }
    }
    return fieldValues.every((value) => value !== null && value !== undefined && value !== '');
  }

  get isRestrictedByProgramExclusivity() {
    if (this.args.model.tasGroups.length === 0) {
      return false;
    }

    return hasInProgressInstanceOfCurrentProgramTemplate(
      this.args.model.tasGroups,
      // @ts-expect-error: not sure
      this.programTemplate.id,
      this.args.model.activeInstances
    );
  }

  featuredSchoolData = trackedFunction(this, async () => {
    const template = this.args.model.programInstance.tasProgramTemplate;
    const schools = await this.store.query<TasProgramTemplateInstitutionModel>(
      'tas-program-template-institution',
      {
        filter: {
          'is-featured': true,
          'tas-program-template': template.id,
        },
        include: 'accredited-institution,unaccredited-institution,logo',
      }
    );
    return schools;
  });

  get featuredSchools() {
    return this.featuredSchoolData.value || [];
  }

  get customEmployeeFields(): CustomFieldSignature[] {
    return this.customFieldsCopy.filter((field) => field.province === 'EMPLOYEE');
  }

  get customProgramFields(): CustomFieldSignature[] {
    return this.customFieldsCopy.filter((field) => field.province === 'PROGRAM');
  }

  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  isSelectedSchoolLpn = async (searchResult: TasUnifiedInstitution) => {
    const lpnSettings = this.partner.companySettings.lpn;
    const isEnabled = lpnSettings?.isEnabled;
    if (!isEnabled) {
      this.isLpnSchoolSelected = false;
      return false;
    }

    if (!this.hasValidDapipId(searchResult)) {
      this.isLpnSchoolSelected = false;
      return false;
    }

    const pages = await this.store.query<LpnPage>('lpn-page', {
      filter: {
        'dapip-id': searchResult.dapipId,
      },
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
    });
    if (pages.length === 0) {
      this.isLpnSchoolSelected = false;
      return false;
    }
    const setting = await pages[0]?.settingForCompany(this.partner.companySettings.id);

    if (!setting) {
      this.isLpnSchoolSelected = true;
      return true;
    }
    if (setting && setting.isEnabled) {
      this.isLpnSchoolSelected = true;
      return true;
    }
    this.isLpnSchoolSelected = false;
    return false;
  };

  updateCustomRequiredFields = (customFieldsCopy: CustomFieldSignature[]) => {
    this.requiredCustomFields = customFieldsCopy.every((field) => {
      if (field.required) {
        // If there is no value return false
        if (field.values.length === 0) {
          return false;
        }
        // Avoid returning false for user supplied false
        return field.values.every((value) => {
          return value !== null && value !== undefined && value !== '';
        });
      }
      return true;
    });
  };

  didSelectSchool = async (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;

    if (!searchResult) {
      this.isLpnSchoolSelected = false;
      return;
    }
    const isLpnSchool = await this.isSelectedSchoolLpn(searchResult);
    if (isLpnSchool) {
      this.isLpnSchoolSelected = true;
    } else {
      this.isLpnSchoolSelected = false;
    }
    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      searchResult?.locationName,
      this.programFieldsCopy
    );
    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      searchResult?.institutionId,
      this.programFieldsCopy
    );
    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      searchResult?.dapipId,
      this.programFieldsCopy
    );
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  toggleDeleteProgram = () => {
    this.isDeleteProgramOpen = !this.isDeleteProgramOpen;
  };

  saveTask = dropTask(async () => {
    try {
      this.args.model.programInstance.fields = this.programFieldsCopy;
      this.args.model.programInstance.customFields = this.customFieldsCopy;
      await this.args.model.programInstance.save();
    } catch (error) {
      console.error('Failed to save program instance:', error);
    }
  });

  getSingleValueForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    return getSingleValueForTasField(
      fieldName as keyof TASProgramInstanceModelFieldsSignature,
      this.programFieldsCopy
    ) as string;
  };

  getAllValuesForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    return getAllValuesForTasField(
      fieldName as keyof TASProgramInstanceModelFieldsSignature,
      this.programFieldsCopy
    ) as string[];
  };

  updateValueForField = (
    fieldName: FieldName,
    fields: TASProgramInstanceModelFieldsSignature,
    type: string | null,
    value: string[] | string | number,
    event?: Event | undefined
  ) => {
    let valueToSet = Array.isArray(value) ? value[0] : value;
    if (getInputmaskAlias(event) === 'currency') {
      valueToSet = Number(getInputmaskUnmaskedValue(event)) * 100;
    }
    if (type === 'number') {
      valueToSet = Math.round(Number(valueToSet));
    }
    if (type === 'date' && typeof valueToSet === 'string') {
      valueToSet = normalizeDate(valueToSet as string);
    }
    // @ts-expect-error: fieldName
    setSingleValueForTasField(fieldName, valueToSet, fields);
  };

  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    updatedFields.forEach((newObject) => {
      const index = existingFields.findIndex(
        (item) => item.label === newObject.label && item.province === newObject.province
      );
      existingFields.splice(index, 1, newObject);
    });

    return existingFields;
  };

  didUpdateEmployeeCustomFields = async (customFields: CustomFieldSignature[]) => {
    let updatedFields = this.updateFieldsWithNewValues(this.customEmployeeFields, customFields);
    this.customFieldsCopy = this.updateFieldsWithNewValues(this.customFieldsCopy, updatedFields);
    this.updateCustomRequiredFields(this.customFieldsCopy);
  };

  didUpdateProgramCustomFields = async (customFields: CustomFieldSignature[]) => {
    let updatedFields = this.updateFieldsWithNewValues(this.customProgramFields, customFields);
    this.customFieldsCopy = this.updateFieldsWithNewValues(this.customFieldsCopy, updatedFields);
    this.updateCustomRequiredFields(this.customFieldsCopy);
  };

  deleteProgram = dropTask(async () => {
    try {
      await this.store.request(
        abandonProgramRequest(
          this.args.model.programInstance.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      // TAS.TODO: temporary routing until tas dashboard is complete
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error(e);
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return agreement.save();
    });
    const saved = await all(promises);
    return saved;
  });

  submitTask = dropTask(async () => {
    if (
      !this.isFormValid ||
      !this.hasAgreedAllConditions ||
      this.isRestrictedByProgramExclusivity
    ) {
      return;
    }
    try {
      this.hasSubmitted = true;
      this.submitError = null;
      await this.saveAgreementsForConditions.perform();
      await this.saveTask.perform();
      await this.store.request(
        requestProgramApprovalRequest(
          this.args.model.programInstance.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      await this.args.model.programInstance.reload();
      this.router.transitionTo(
        'authenticated.tas.programs.show',
        this.args.model.programInstance.id
      );
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  <template>
    <VStack>
      <Header class="flex justify-between items-center">
        <div>
          {{! TAS.TODO: temporary routing until tas dashboard is complete }}
          <LinkTo class="flex justify-left items-center gap-4" @route="authenticated.tas.dashboard">
            <PhCaretLeft @size="20" class="inline" />
            {{t "dashboard"}}
          </LinkTo>
        </div>
        <div class="flex items-center gap-4">
          <Button
            data-test-save-application
            @intent="primary"
            @appearance="outlined"
            @isRunning={{this.saveTask.isRunning}}
            {{on "click" this.saveTask.perform}}
          >
            {{t "common.save"}}
          </Button>
          <Button
            @intent="danger"
            @appearance="outlined"
            data-test-delete-application
            {{on "click" this.toggleDeleteProgram}}
          >
            <PhTrash @size="20" class="inline" />
          </Button>
        </div>
      </Header>
      <HStack class="items-start">
        <Section class="w-full md:w-2/3 md:flex-none">
          <:header>
            <div>{{t "tas.program.approval"}}</div>
            <div class="font-normal">
              <span class="text-error-400 text-xl">*</span>
              {{t "tas.program.required_items"}}
            </div>
            {{#unless this.isActiveParticipant}}
              <AlertBanner
                @intent="warning"
                @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                class="my-4"
              >
                <p class="text-sm">
                  {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                </p>
              </AlertBanner>
            {{/unless}}
          </:header>
          <:body>
            {{#each this.programFormFields as |programField|}}
              {{#if (this.isSchoolSelectProgramField programField.name)}}
                <SchoolSelect
                  @programTemplate={{this.programTemplate}}
                  @labelClass="!text-gray-600 font-semibold !text-base w-1/2"
                  @selected={{this.selectedSchool}}
                  @onChange={{this.didSelectSchool}}
                  class="mb-2 flex flex-wrap justify-between gap-y-4 items-center [&>div]:w-1/2 [&>div]:border [&>div]:border-default-400 [&>div]:rounded-lg"
                />
                {{#if this.isLpnSchoolSelected}}
                  <AlertBanner
                    @intent="info"
                    @title={{t "lpn.application_banner.title"}}
                    class="my-4"
                  >
                    <p class="text-sm">
                      {{t "lpn.application_banner.body"}}
                    </p>
                  </AlertBanner>
                {{/if}}
                <Divider class="my-4" />
              {{else}}
                <ProgramFormField
                  @name={{programField.name}}
                  @programTemplate={{this.programTemplate}}
                  @labelClass="!text-gray-600 font-semibold !text-base w-1/2"
                  class="flex flex-wrap justify-between gap-y-4 items-center [&>div]:w-1/2"
                  as |field|
                >
                  {{#if field.isFieldOptionPicklist}}
                    <ReSelect
                      data-test={{field.name}}
                      @items={{field.fieldOptions}}
                      @selected={{this.getSingleValueForField field.name}}
                      @onChange={{fn
                        this.updateValueForField
                        field.name
                        this.programFieldsCopy
                        programField.type
                      }}
                      @disabled={{programField.disabled}}
                      @placeholder="Select an option"
                      @clearable={{false}}
                    />

                  {{else if (eq programField.mask "currency")}}
                    <Input
                      data-test={{field.name}}
                      id={{field.inputId}}
                      @value={{divide
                        (or
                          (number (getSingleValueForTasField field.name this.programFieldsCopy)) 0
                        )
                        100
                      }}
                      @onChange={{fn
                        this.updateValueForField
                        field.name
                        this.programFieldsCopy
                        programField.type
                      }}
                      disabled={{programField.disabled}}
                      {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
                      {{!-- @errors={{errorsForField field.name schemaErrors=this.formValidationErrors}} --}}
                    />
                  {{else if (eq programField.type "date")}}
                    <span class="w-1/2 relative">
                      <EmberFlatpickr
                        {{! @glint-expect-error: FieldValue type is broader than DateOption }}
                        @date={{or
                          (getSingleValueForTasField field.name this.programFieldsCopy)
                          ""
                        }}
                        @onChange={{fn this.updateDateFieldValue field.name}}
                        @dateFormat="Y-m-d"
                        @altInput={{true}}
                        @altFormat="F j, Y"
                        @disableMobile={{true}}
                        placeholder="mm/dd/yyyy"
                        data-test={{field.name}}
                        disabled={{programField.disabled}}
                        class="tio-form-input-lg"
                      />
                      <div
                        class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                      >
                        <PhCalendarBlank @size="20" />
                      </div>
                    </span>
                  {{else}}
                    <Input
                      data-test={{field.name}}
                      id={{field.inputId}}
                      @value={{getSingleValueForTasField field.name this.programFieldsCopy}}
                      @onChange={{fn
                        this.updateValueForField
                        field.name
                        this.programFieldsCopy
                        programField.type
                      }}
                      type={{programField.type}}
                      disabled={{programField.disabled}}
                    />
                  {{/if}}
                  <Divider class="mb-4" />
                </ProgramFormField>
              {{/if}}
            {{/each}}
            <TuitionAssistanceFormsCustomFieldsForProvince
              @province="EMPLOYEE"
              @customFields={{this.customEmployeeFields}}
              @didUpdateFields={{this.didUpdateEmployeeCustomFields}}
              @formValidationErrors={{this.customFieldValidationErrors}}
              @fieldContainerClass="pb-4"
              @programTemplate={{this.programTemplate}}
            />
            <TuitionAssistanceFormsCustomFieldsForProvince
              @province="PROGRAM"
              @customFields={{this.customProgramFields}}
              @didUpdateFields={{this.didUpdateProgramCustomFields}}
              @formValidationErrors={{this.customFieldValidationErrors}}
              @fieldContainerClass="pb-4"
              @programTemplate={{this.programTemplate}}
            />
            {{! Upload Attachments}}
            {{#if this.shouldShowAttachments}}
              <div
                id="attachments-section"
                class="my-10 rounded-lg transition-all duration-300
                  {{if
                    (eq this.highlightedSection 'attachments-section')
                    'ring-4 ring-violet-500 ring-offset-2'
                  }}"
              >
                <div>
                  <h2 class="text-grey-600 text-2xl">
                    {{t "common.attachments"}}
                    {{#if this.isAttachmentRequired}}
                      <span class="text-error-400 text-2xl">*</span>
                    {{/if}}
                  </h2>
                </div>
                <div class="flex items-center w-full md:max-w-lg mb-4">
                  <UploadAttachments
                    @programTemplate={{this.programTemplate}}
                    @classification="PROGRAM_APPROVAL_DOCUMENTS"
                    @attachable={{@model.programInstance}}
                    @attachableType="tasProgramInstance"
                    @useDropzone={{true}}
                    @hideDiscarded={{true}}
                    {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                  />
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
        <VStack class="w-full md:w-1/3 md:flex-none">
          {{#if this.isLpnEnabled}}
            <Section>
              <LpnInfoCard />
            </Section>
          {{/if}}
          <Section>
            <:header>
              <header
                class="bg-tio-gray-25 flex items-center gap-2 py-2 px-5 text-sm font-medium tracking-wide -m-4"
              >
                <PhClipboardText @size="32" />
                <span>{{t "tas.benefits_summary.required_items"}}</span>
              </header>
            </:header>
            <:body>
              {{! Required Items Section - ASIDE}}
              <section class="pb-5">
                {{#if this.isAttachmentRequired}}
                  <div>
                    <div class="flex flex-row justify-between items-center">
                      <button
                        type="button"
                        class="flex items-center gap-3 cursor-pointer group bg-transparent border-0 p-0 text-left"
                        aria-label={{t "tas.benefits_summary.attachments"}}
                        {{on "click" (fn this.scrollTo "attachments-section")}}
                      >
                        {{#if this.hasRequiredAttachments}}
                          <PhCheckCircle
                            @size="24"
                            @weight="fill"
                            class="text-violet-500 shrink-0"
                          />
                        {{else}}
                          <PhCircle
                            @size="24"
                            class="text-tio-gray-200 group-hover:text-violet-500 shrink-0 transition-colors"
                          />
                        {{/if}}
                        <span
                          class="group-hover:text-violet-600 group-hover:underline transition-colors"
                        >{{t "tas.benefits_summary.attachments"}}</span>
                      </button>
                      <div>
                        <UploadAttachments
                          @programTemplate={{this.programTemplate}}
                          @classification="PROGRAM_APPROVAL_DOCUMENTS"
                          @attachable={{@model.programInstance}}
                          @attachableType="tasProgramInstance"
                          @useDropzone={{false}}
                          @iconOnlyUpload={{true}}
                          @hideInstructions={{true}}
                          @hideAttachments={{true}}
                          {{!-- @disabled={{this.disableAttachmentUpload}} --}}
                        />
                      </div>
                    </div>
                    {{#if this.attachmentsListDescription}}
                      <div class="flex items-center gap-3 -mt-1">
                        {{#if this.hasRequiredAttachments}}
                          <PhCheckCircle
                            @size="24"
                            @weight="fill"
                            class="text-violet-500 shrink-0"
                          />
                        {{else}}
                          <PhCircle @size="24" class="text-tio-gray-200 shrink-0" />
                        {{/if}}
                        <p>{{this.attachmentsListDescription}}</p>
                      </div>
                    {{/if}}
                  </div>
                {{/if}}

                {{#if this.conditionsForSubmit.length}}
                  <AcceptConditions
                    @conditions={{this.conditionsForSubmit}}
                    @onChange={{this.didUpdateAcceptedConditions}}
                  />
                {{/if}}
              </section>

              <div class="pb-4 flex flex-col gap-2 items-center w-full">
                <Button
                  @isRunning={{this.submitTask.isRunning}}
                  class="w-2/3"
                  @intent="primary"
                  data-test-submit-program
                  {{on "click" this.submitTask.perform}}
                  @isDisabled={{not this.canSubmitApplication}}
                >
                  {{t "common.next"}}
                </Button>

                <p class="text-sm font-light text-center">
                  {{t "tas.benefits_summary.helper_text"}}
                </p>
                <ErrorMessages
                  @showErrors={{this.hasSubmitted}}
                  @error={{this.submitError}}
                  class="text-sm"
                />
              </div>
            </:body>
          </Section>
        </VStack>
      </HStack>
    </VStack>

    <Modal
      @isOpen={{this.isDeleteProgramOpen}}
      @onClose={{this.toggleDeleteProgram}}
      data-test-delete-program-modal
    >
      <:header>{{t "tas.program.delete_program"}}</:header>
      <:body>
        <p>{{t "tas.program.confirm_program_delete"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteProgram.isRunning}}
          @onCancel={{this.toggleDeleteProgram}}
          @onSubmit={{this.deleteProgram.perform}}
        />
      </:footer>
    </Modal>
  </template>
}
